import { useState } from 'react';
import { Button, Divider, Stack, Text } from '@mantine/core';
import { IconBuildingChurch } from '@tabler/icons-react';
import { Callout } from '@/components/ui/Callout';
import { useAuth } from '@actschurch/ccms-sdk';
import { useCopy, useRidesSettings } from '@/lib/settings';
import { planningCentreCallbackUrl, rememberPlanningCentreRedirect } from '@/lib/planningCentre';

/**
 * "Continue with Planning Centre": same flow as ccms_v2 and the Resource Centre. Hidden when
 * the admin switches PlanningCentreLoginEnabled off. New Planning Centre people get a CCMS
 * account automatically (Member role); an existing account with the same email is linked after
 * the member confirms their password on the callback page.
 */
export function PlanningCentreButton({ redirectTo, position = 'top' }: { redirectTo: string; position?: 'top' | 'bottom' }) {
  const { planningCentre } = useAuth();
  const { settings } = useRidesSettings();
  const { t } = useCopy();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!settings.planningCentreLoginEnabled) return null;

  const onClick = async () => {
    setStarting(true);
    setError(null);
    try {
      rememberPlanningCentreRedirect(redirectTo);
      const { authUrl } = await planningCentre.start(redirectTo, planningCentreCallbackUrl());
      window.location.assign(authUrl);
    } catch {
      setError("We couldn't open Planning Centre. Please try again, or use your email below.");
      setStarting(false);
    }
  };

  const button = (
    <Stack gap={6}>
      {error && <Callout tone="bad" live>{error}</Callout>}
      <Button variant="default" size="md" fullWidth leftSection={<IconBuildingChurch size={20} />} loading={starting} onClick={onClick}>
        {t('login.planning_centre_button')}
      </Button>
      <Text size="sm" c="dimmed">{t('login.planning_centre_note')}</Text>
    </Stack>
  );

  return position === 'top' ? (
    <Stack gap="md" mb="md">
      {button}
      <Divider label="or use your email address" labelPosition="center" />
    </Stack>
  ) : (
    <Stack gap="md" mt="md">
      <Divider label="or" labelPosition="center" />
      {button}
    </Stack>
  );
}
